import { defineComponent as _defineComponent } from 'vue'
import { createCommentVNode as _createCommentVNode, createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, normalizeClass as _normalizeClass, Fragment as _Fragment, createVNode as _createVNode, renderList as _renderList, createBlock as _createBlock, toDisplayString as _toDisplayString, normalizeStyle as _normalizeStyle, createTextVNode as _createTextVNode, withCtx as _withCtx } from "vue"

const _hoisted_1 = ["disabled"]
const _hoisted_2 = ["disabled"]
const _hoisted_3 = ["disabled"]
const _hoisted_4 = {
  key: 0,
  class: "ifc-editor__loading"
}
const _hoisted_5 = { class: "ifc-editor__loading-card" }
const _hoisted_6 = { style: {"margin-top":"10px"} }
const _hoisted_7 = { class: "ifc-editor__progress-track" }

import {
        ref,
        reactive,
        computed,
        onMounted,
        onUnmounted,
        nextTick,
        createApp,
        type App,
    } from 'vue';
    import * as THREE from 'three';
    import { TransformControls } from 'three/examples/jsm/controls/TransformControls.js';
    import * as OBC from '@thatopen/components';
    import * as OBF from '@thatopen/components-front';
    import * as WebIFC from 'web-ifc';
    import { Notifier } from 'Core/Common/Notifier';
    import { Modal } from 'Core/Common/Modal';
    import { IfcViewerCore, ViewerTool } from 'Core/Components/Ifc/IfcViewerCore';
    import IfcTreeNode from 'Core/Components/Ifc/IfcTreeNode.vue';
    import IfcPropertiesPanel from 'Core/Components/Ifc/IfcPropertiesPanel.vue';
    import IfcWorkbench from 'Core/Components/Ifc/IfcWorkbench.vue';
    import IfcViewerToolbar from 'Core/Components/Ifc/IfcViewerToolbar.vue';
    import IfcClassVisibilityBar from 'Core/Components/Ifc/IfcClassVisibilityBar.vue';
    import BsddInfoModal from 'Core/Components/Ifc/BsddInfoModal.vue';
    import { IfcApi } from 'Core/Components/Ifc/IfcApi';
    import type { IfcNodeDto } from 'api/IfcParser/Application/Models/IfcNodeDto';
    import type { IfcPropertyDto } from 'api/IfcParser/Application/Models/IfcPropertyDto';

    /**
     * Standalone IFC editor page (Vue replacement for the previous Knockout
     * IfcEditor). Loads an IFC from the user's disk, renders via the shared
     * <IfcViewerCore>, builds a tree + property panel from web-ifc, and offers
     * length/angle/area/clip measurements, transform controls, and annotations.
     *
     * The renderer is shared with IfcImporterPage and DocumentManager; this page
     * adds the editor-only features (file open, IFC export, property editing,
     * transform gizmo, marker annotations) on top of that foundation.
     */

    type EditorTool = 'select' | 'move' | 'length' | 'angle' | 'area' | 'clip' | 'annotate';

    /** Per-property writeback metadata for the editable property panel. Keyed by
     *  `{psetName}::{propName}` so the change handler knows which web-ifc line to
     *  update. */
    interface PropEditMeta {
        expressId: number;
        propName: string;
    }

    /**
     * Accepted props:
     *  - initialBytes / initialFileName: when set (DocumentManager modal flow),
     *    skip the file-picker and auto-load these bytes once the viewer mounts.
     *  - parseRecord: when set, parse the tree server-side from the record's
     *    DOCUMENT field instead of uploading the bytes back. Skips a full
     *    download → re-upload round trip in the modal flow.
     *  - inModal: show the close button + emit `close` on click. False (default)
     *    when the page is mounted full-screen via the menu — there's no host to
     *    close.
     *  - highlightGlobalId: when set, auto-isolate this element after the model
     *    loads. Used by DocumentManager's "Show in root model" flow to land on
     *    the original article inside the full-model view.
     */
    
export default /*@__PURE__*/_defineComponent({
  __name: 'IfcViewer',
  props: {
    initialBytes: {},
    initialFileName: {},
    parseRecord: {},
    inModal: { type: Boolean },
    highlightGlobalId: {}
  },
  emits: ["close"],
  setup(__props: any, { emit: __emit }) {

    const props = __props;

    const emit = __emit;

    const notifier = new Notifier();

    // ── State ─────────────────────────────────────────────────────────────────
    const activeTool = ref<EditorTool>('select');
    const transformMode = ref<'translate' | 'rotate' | 'scale'>('translate');
    const modelLoaded = ref(false);
    const loading = ref(false);
    const loadingMessage = ref('');
    const loadingPercent = ref(0);
    const statusText = ref('Ready');
    // Hierarchical tree (same shape as the IfcImporter's server-parsed payload),
    // rendered by the shared <IfcTreeNode> component. Built client-side via web-ifc.
    const tree = ref<IfcNodeDto[]>([]);
    const expandedIds = reactive<Record<number, boolean>>({});
    const selectedNode = ref<IfcNodeDto | null>(null);
    // Map of GlobalId → ARTICLES record id, for tree nodes that already
    // correspond to an imported article. When set, IfcTreeNode renders an
    // external-link icon and emits `openExisting` on click; we route that to
    // the global PubSub to open the record screen in a modal.
    const existingRecords = ref<Record<string, number>>({});
    const existingEntityId = ref<number>(0);

    const workbench = ref<InstanceType<typeof IfcWorkbench> | null>(null);
    const fileInput = ref<HTMLInputElement | null>(null);

    // GlobalId → IfcNodeDto map, populated from the server-parsed tree so
    // viewport picks can resolve a clicked element back to its tree node.
    const globalIdToTreeNode = new Map<string, IfcNodeDto>();
    // Per-property writeback metadata, populated lazily when a node is selected
    // (so the property panel can write edits back through web-ifc).
    const propEditMap = new Map<string, PropEditMeta>();

    // ── Renderer + editor extras ──────────────────────────────────────────────
    let viewer: IfcViewerCore | null = null;
    let ifcApi: WebIFC.IfcAPI | null = null;
    let modelID = -1;
    // Raw IFC bytes for the LAZY editor parse: web-ifc drives property edits
    // and export, but its full second parse runs on first use, not on open.
    let editorBytes: Uint8Array | null = null;

    async function ensureEditorModel(): Promise<boolean> {
        if (ifcApi && modelID >= 0) return true;
        if (!editorBytes) return false;
        statusText.value = 'Preparing editor...';
        ifcApi = new WebIFC.IfcAPI();
        await ifcApi.Init();
        modelID = ifcApi.OpenModel(editorBytes);
        statusText.value = '';
        return true;
    }
    let ifcBytes: Uint8Array | null = null;

    let marker: OBF.Marker | null = null;
    let transformControls: TransformControls | null = null;
    let selectedMesh: THREE.Object3D | null = null;
    const annotations: Array<{ id: string; text: string; position: THREE.Vector3 }> = [];

    let suppressClear = false;
    let dblclickHandler: ((e: MouseEvent) => void) | null = null;

    // ── Lifecycle ─────────────────────────────────────────────────────────────
    onMounted(async () => {
        window.addEventListener('keydown', onKeyDown);

        await nextTick();
        const host = workbench.value?.viewportEl as HTMLDivElement | undefined;
        if (!host) return;

        viewer = new IfcViewerCore({
            showGrid: true,
            showGizmo: true,
            enableTools: true,
            hoverHighlight: false, // editor uses click-select, not hover
        });
        await viewer.mount(host);

        // Selection from the 3D viewport: viewer hands us a GlobalId, we resolve
        // it to a tree node and load its properties via web-ifc.
        viewer.onElementSelected = (globalId: string | null) => {
            if (suppressClear) return;
            if (globalId === null) {
                clearSelection();
                return;
            }
            const node = globalIdToTreeNode.get(globalId);
            if (node) {
                // Make sure the row is reachable in the tree before we scroll
                // — the user may have collapsed a parent. expandPathTo opens
                // every ancestor on the path; selectByNode highlights the row
                // and loads its properties.
                expandPathTo(globalId);
                selectByNode(node);
                scrollTreeRowIntoView(node.expressId);
            }

            // Capture the loaded model's THREE.Object3D for the move tool.
            const model = viewer?.getCurrentModel();
            if (model) {
                selectedMesh = model.object;
                if (activeTool.value === 'move') attachTransform(model.object);
            }
        };

        const components = viewer.getComponents();
        const world = viewer.getWorld();
        if (!components || !world || !world.renderer) return;

        // Annotations — owned by the editor, not the viewer.
        marker = components.get(OBF.Marker);

        // Transform controls (move/rotate/scale).
        const renderer = world.renderer.three;
        transformControls = new TransformControls(world.camera.three, renderer.domElement);
        transformControls.setSize(0.75);
        const helper = transformControls.getHelper();
        helper.visible = false;
        world.scene.three.add(helper);

        // Disable camera orbit while dragging the transform gizmo.
        transformControls.addEventListener('dragging-changed', (event: any) => {
            (world.camera.controls as any).enabled = !event.value;
        });
        transformControls.addEventListener('objectChange', () => {
            statusText.value = 'Transforming...';
        });

        // Double-click for annotations only — the viewer already double-click-
        // creates measurement/clipper geometry when one of those tools is on.
        dblclickHandler = (e: MouseEvent) => onDoubleClick(e);
        host.addEventListener('dblclick', dblclickHandler);

        statusText.value = 'Viewer ready — open an IFC file to begin';

        // Auto-load when the host hands us bytes (DocumentManager modal flow).
        if (props.initialBytes) {
            // Vue wraps props in reactive proxies. web-ifc's WASM bridge doesn't
            // handle proxied typed arrays — `OpenModel` succeeds but later
            // `GetLine` calls on children silently fail, so only IfcProject
            // makes it into the tree. Hand it a fresh non-reactive copy.
            const bytes = new Uint8Array(props.initialBytes);
            await loadFromBytes(bytes, props.initialFileName ?? 'document.ifc');
        }
    });

    onUnmounted(() => {
        window.removeEventListener('keydown', onKeyDown);
        const host = workbench.value?.viewportEl as HTMLDivElement | undefined;
        if (host && dblclickHandler) {
            host.removeEventListener('dblclick', dblclickHandler);
        }
        try {
            bsddVueApp?.unmount();
            bsddModal?.Destroy();
        } catch {
            /* ignore */
        }
        bsddVueApp = null;
        bsddModal = null;
        try {
            if (transformControls) {
                const world = viewer?.getWorld();
                transformControls.detach();
                world?.scene.three.remove(transformControls.getHelper());
                transformControls.dispose();
            }
        } catch {
            /* ignore */
        }
        if (modelID >= 0 && ifcApi) {
            try {
                ifcApi.CloseModel(modelID);
            } catch {}
        }
        try {
            viewer?.dispose();
        } catch {}
        viewer = null;
    });

    // ── Keyboard ──────────────────────────────────────────────────────────────
    function onKeyDown(e: KeyboardEvent) {
        switch (e.key) {
            case 'Escape':
                setTool('select');
                break;
            case 'Delete':
            case 'Backspace':
                viewer?.clearMeasurements();
                statusText.value = 'Deleted';
                break;
            case 'g':
            case 'G':
                if (activeTool.value === 'move') setTransformMode('translate');
                break;
            case 'r':
            case 'R':
                if (activeTool.value === 'move') setTransformMode('rotate');
                break;
            case 's':
            case 'S':
                if (activeTool.value === 'move') setTransformMode('scale');
                break;
        }
    }

    function onDoubleClick(_e: MouseEvent) {
        if (activeTool.value === 'annotate') createAnnotation();
    }

    async function createAnnotation() {
        if (!viewer || !marker) return;
        const components = viewer.getComponents();
        const world = viewer.getWorld();
        if (!components || !world) return;

        const raycaster = components.get(OBC.Raycasters).get(world);
        const result = await raycaster.castRay();
        if (!result || !result.point) return;

        const text = prompt('Enter annotation text:');
        if (!text) return;

        const id = `ann_${Date.now()}`;
        const markerEl = document.createElement('div');
        markerEl.style.cssText =
            'background: #ffd700; color: #000; padding: 4px 8px; border-radius: 4px; font-size: 11px; cursor: pointer; max-width: 150px; word-wrap: break-word; box-shadow: 0 2px 4px rgba(0,0,0,0.3);';
        markerEl.textContent = text;

        marker.create(world, markerEl, result.point, false);
        annotations.push({ id, text, position: result.point.clone() });
        statusText.value = `Annotation added: "${text.substring(0, 20)}..."`;
    }

    // ── File operations ───────────────────────────────────────────────────────
    function openFile() {
        fileInput.value?.click();
    }

    async function onFileSelected(event: Event) {
        const input = event.target as HTMLInputElement;
        if (!input.files || input.files.length === 0) return;

        const file = input.files[0];
        statusText.value = `Loading ${file.name}...`;
        const arrayBuffer = await file.arrayBuffer();
        await loadFromBytes(new Uint8Array(arrayBuffer), file.name);
        input.value = '';
    }

    async function loadFromBytes(data: Uint8Array, fileName: string) {
        ifcBytes = data;
        loading.value = true;
        loadingPercent.value = 0;
        loadingMessage.value = 'Initializing...';

        try {
            // Server-side tree (xBIM) — single source of truth, shared with the
            // IFC importer. Walks the FULL spatial hierarchy including
            // IfcRelContainedInSpatialStructure, so per-element sub-IFCs (where
            // the client-side web-ifc walker would only find IfcProject) still
            // surface their actual element under the project node.
            //
            // Two paths:
            //   - parseRecord set (modal flow) → server reads DOCUMENT bytes
            //     straight from the DB, no upload.
            //   - otherwise (menu file-picker) → chunk-upload, then parse.
            loadingMessage.value = 'Parsing on server...';
            loadingPercent.value = 5;
            const onParseComplete = (result: { tree?: IfcNodeDto[] }) => {
                tree.value = result.tree ?? [];
                rebuildGlobalIdIndex(tree.value);
            };

            await new Promise<void>((resolve, reject) => {
                let parseError = '';
                let gotComplete = false;
                let nodeCount = 0;
                const callbacks = {
                    onNode: () => {
                        nodeCount++;
                    },
                    onComplete: (result: { tree?: IfcNodeDto[] }) => {
                        gotComplete = true;
                        onParseComplete(result);
                    },
                    onError: (msg: string) => {
                        parseError = msg;
                        // eslint-disable-next-line no-console
                        console.error('[IfcViewer] parse error event:', msg);
                    },
                };

                const parsePromise = props.parseRecord
                    ? IfcApi.ParseRecordStream(props.parseRecord, callbacks)
                    : (async () => {
                          loadingMessage.value = 'Uploading to server...';
                          const file = new File([new Uint8Array(data) as any], fileName, {
                              type: 'application/octet-stream',
                          });
                          await IfcApi.Upload(file, (percent, message) => {
                              loadingPercent.value = 5 + Math.round(percent * 0.25);
                              loadingMessage.value = message;
                          });
                          loadingMessage.value = 'Parsing on server...';
                          loadingPercent.value = 30;
                          await IfcApi.ParseStream(fileName, callbacks);
                      })();

                parsePromise
                    .then(() => {
                        if (parseError) {
                            reject(new Error(parseError));
                            return;
                        }
                        if (!gotComplete) {
                            reject(
                                new Error(
                                    `Server closed the parse stream without a complete event ` +
                                        `(received ${nodeCount} node events). ` +
                                        `Check the API log for an exception in ParseRecordStream.`
                                )
                            );
                            return;
                        }
                        resolve();
                    })
                    .catch(reject);
            });

            // Auto-expand top two levels (Project + Site usually).
            Object.keys(expandedIds).forEach((k) => delete expandedIds[+k]);
            const expand = (nodes: IfcNodeDto[], depth: number) => {
                for (const n of nodes) {
                    if (depth < 2) {
                        expandedIds[n.expressId] = true;
                        expand(n.children, depth + 1);
                    }
                }
            };
            expand(tree.value, 0);

            // Lookup which tree nodes already exist as ARTICLES records, so
            // each row gets a tap-to-open-record link icon. Non-fatal —
            // skipping it just hides the link, the rest of the viewer keeps
            // working.
            try {
                const allIds: string[] = [];
                const collectIds = (nodes: IfcNodeDto[]) => {
                    for (const n of nodes) {
                        if (n.globalId) allIds.push(n.globalId);
                        if (n.children?.length) collectIds(n.children);
                    }
                };
                collectIds(tree.value);
                if (allIds.length > 0) {
                    const existing = await new Promise<{
                        entityId?: number;
                        records?: Record<string, number>;
                    }>((resolve, reject) => {
                        IfcApi.FindExisting(allIds).then(resolve).fail(reject);
                    });
                    existingEntityId.value = existing.entityId ?? 0;
                    existingRecords.value = existing.records ?? {};
                }
            } catch {
                /* tolerate — link icons just won't appear */
            }

            // web-ifc still drives in-place property edits and IFC export,
            // but that second full parse belongs on first USE (export /
            // property edit), not on open — eager it doubled the viewer's
            // critical path. Stash the bytes; ensureEditorModel() lazy-inits.
            editorBytes = ifcBytes;

            loadingMessage.value = 'Loading 3D...';
            loadingPercent.value = 70;
            // Live IFC→fragments conversion progress (70..99); a fragment-cache
            // hit reports 1 immediately and jumps straight to finalizing.
            await viewer?.loadBytes(ifcBytes, 'ifc', (fraction) => {
                loadingPercent.value = Math.min(99, 70 + Math.round(fraction * 29));
                loadingMessage.value = fraction >= 1
                    ? 'Finalizing 3D...'
                    : `Loading 3D... ${Math.round(fraction * 100)}%`;
            });

            modelLoaded.value = true;
            loadingPercent.value = 100;
            loading.value = false;
            statusText.value = `${fileName} — ${tree.value.length} root(s) loaded`;

            // DocumentManager "Show in root model" flow: keep the full model
            // visible and just highlight + frame the descendant element so
            // the user sees it in context. (Earlier we isolated to it, but
            // that hides everything else and defeats the purpose of opening
            // the root model.)
            if (props.highlightGlobalId) {
                const targetNode = globalIdToTreeNode.get(props.highlightGlobalId);
                if (targetNode) {
                    // Expand every ancestor in the tree so the target row is
                    // actually visible — auto-expand only opens the top two
                    // levels, and the target may be deeper (Building → Storey
                    // → Space → Furniture).
                    expandPathTo(props.highlightGlobalId);
                    selectByNode(targetNode);
                    await scrollTreeRowIntoView(targetNode.expressId);
                }
                try {
                    await viewer?.highlightByGlobalId(props.highlightGlobalId);
                    // Position the camera in front of the highlighted element
                    // (looking down +Z toward the bbox center). The selection
                    // map populated by highlightByGlobalId drives setView so
                    // it frames just this element, not the whole model.
                    await viewer?.setView('front');
                } catch {
                    /* element not in this model — leave the full view */
                }
            }
        } catch (err) {
            loading.value = false;
            notifier.Failed(`Failed to load: ${(err as Error).message}`);
        }
    }

    /** Re-populate the GlobalId → IfcNodeDto map by walking the server tree.
     *  The viewport's pick callback uses this map to resolve a clicked
     *  GlobalId back to the tree node. */
    function rebuildGlobalIdIndex(nodes: IfcNodeDto[]) {
        globalIdToTreeNode.clear();
        const walk = (list: IfcNodeDto[]) => {
            for (const n of list) {
                if (n.globalId) globalIdToTreeNode.set(n.globalId, n);
                if (n.children?.length) walk(n.children);
            }
        };
        walk(nodes);
    }

    /** Expand every ancestor of the node identified by `globalId` so the row
     *  is visible in the tree. No-op if the GlobalId isn't in the current
     *  tree. */
    function expandPathTo(globalId: string) {
        const findPath = (list: IfcNodeDto[], trail: IfcNodeDto[]): IfcNodeDto[] | null => {
            for (const n of list) {
                const next = [...trail, n];
                if (n.globalId === globalId) return next;
                if (n.children?.length) {
                    const found = findPath(n.children, next);
                    if (found) return found;
                }
            }
            return null;
        };
        const path = findPath(tree.value, []);
        if (!path) return;
        // Expand every node on the path EXCEPT the target itself — leaving
        // the target collapsed avoids force-opening a massive subtree under
        // a Storey/Space when the user really just wants to see that one row.
        for (let i = 0; i < path.length - 1; i++) {
            expandedIds[path[i].expressId] = true;
        }
    }

    /**
     * Scroll the tree row matching `expressId` into view in the tree pane.
     *
     * We do NOT use Element.scrollIntoView — it walks every scrollable
     * ancestor (CSSOM-View "scroll an element into view" algorithm), which
     * can move the modal/page instead of just the tree pane.
     *
     * Instead we compute the delta with getBoundingClientRect — robust
     * under flex layouts, unlike offsetTop which is relative to the nearest
     * positioned ancestor and lies inside flex columns — and scroll only the
     * tree dock body. Smallest possible side effect:
     *   - touches only .ifc-dock__body--tree, never the modal or page;
     *   - no animation, so no in-flight smooth scroll competing with wheel;
     *   - early-returns when the row is already visible (if-needed
     *     semantic).
     *
     * nextTick + 2× requestAnimationFrame guards against measuring before
     * the modal's flex children have laid out (Vue's nextTick fires before
     * the browser has run layout / paint).
     */
    async function scrollTreeRowIntoView(expressId: number) {
        await nextTick();
        await new Promise((r) => requestAnimationFrame(() => r(undefined)));
        await new Promise((r) => requestAnimationFrame(() => r(undefined)));

        const root = workbench.value?.$el as HTMLElement | undefined;
        if (!root) return;
        const row = root.querySelector<HTMLElement>(
            `.ifc-tree-node[data-express-id="${expressId}"]`
        );
        if (!row) return;
        const pane = row.closest<HTMLElement>('.ifc-dock__body--tree');
        if (!pane || pane.clientHeight === 0) return;
        const maxScrollTop = pane.scrollHeight - pane.clientHeight;
        if (maxScrollTop <= 0) return;

        const paneRect = pane.getBoundingClientRect();
        const rowRect = row.getBoundingClientRect();
        const margin = 8;
        const isAbove = rowRect.top < paneRect.top + margin;
        const isBelow = rowRect.bottom > paneRect.bottom - margin;
        if (!isAbove && !isBelow) return; // already visible

        // Center the row in the pane.
        const targetTop =
            pane.scrollTop + rowRect.top - paneRect.top - (pane.clientHeight - rowRect.height) / 2;
        pane.scrollTo({
            top: Math.max(0, Math.min(maxScrollTop, targetTop)),
            behavior: 'auto',
        });
    }

    async function exportIfc() {
        if (!(await ensureEditorModel())) return;
        try {
            const data = ifcApi.SaveModel(modelID);
            const blob = new Blob([new Uint8Array(data) as any], {
                type: 'application/octet-stream',
            });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = 'exported.ifc';
            a.click();
            URL.revokeObjectURL(url);
            notifier.Success('IFC file exported');
        } catch (err) {
            notifier.Failed(`Export failed: ${(err as Error).message}`);
        }
    }

    // ── Tool switching ────────────────────────────────────────────────────────
    function setTool(tool: EditorTool) {
        viewer?.setTool('none');
        detachTransform();
        activeTool.value = tool;

        switch (tool) {
            case 'length':
                viewer?.setTool('length' as ViewerTool);
                statusText.value = 'Click two points to measure distance';
                break;
            case 'angle':
                viewer?.setTool('angle' as ViewerTool);
                statusText.value = 'Click three points to measure angle';
                break;
            case 'area':
                viewer?.setTool('area' as ViewerTool);
                statusText.value = 'Click points to measure area';
                break;
            case 'clip':
                viewer?.setTool('clip' as ViewerTool);
                statusText.value = 'Double-click to create clipping plane';
                break;
            case 'select':
                statusText.value = 'Click elements to select';
                break;
            case 'move':
                statusText.value =
                    'Select element, then drag gizmo to move. Press R for rotate, S for scale, G for translate.';
                if (selectedMesh) attachTransform(selectedMesh);
                break;
            case 'annotate':
                statusText.value = 'Double-click to place annotation';
                break;
        }
    }

    function setTransformMode(mode: 'translate' | 'rotate' | 'scale') {
        transformMode.value = mode;
        transformControls?.setMode(mode);
        statusText.value = `Transform: ${mode}`;
    }

    function attachTransform(object: THREE.Object3D) {
        if (!transformControls) return;
        transformControls.attach(object);
        transformControls.getHelper().visible = true;
        transformControls.setMode(transformMode.value);
    }

    function detachTransform() {
        if (!transformControls) return;
        transformControls.detach();
        transformControls.getHelper().visible = false;
        selectedMesh = null;
    }

    // ── View ──────────────────────────────────────────────────────────────────
    const ghostMode = ref(false);

    // Class visibility — derived from the parsed tree so the icon bar can list
    // every distinct IfcClass present in the model. Toggles delegate to
    // IfcViewerCore.hideByClass.
    const hiddenClasses = reactive<Record<string, boolean>>({});
    const allClasses = computed(() => {
        const set = new Set<string>();
        const walk = (nodes: IfcNodeDto[]) => {
            for (const n of nodes) {
                if (n.ifcClass) set.add(n.ifcClass);
                walk(n.children);
            }
        };
        walk(tree.value);
        return Array.from(set).sort();
    });

    async function toggleClassVisibility(cls: string) {
        hiddenClasses[cls] = !hiddenClasses[cls];
        await viewer?.hideByClass([cls], hiddenClasses[cls]);
    }

    function fitView() {
        viewer?.fitToSelection();
    }

    function showAll() {
        viewer?.showAll();
    }

    function setView(preset: 'top' | 'front' | 'side' | 'iso') {
        viewer?.setView(preset);
    }

    function resetRotation() {
        viewer?.resetRotation();
    }

    function toggleGhost() {
        ghostMode.value = !ghostMode.value;
        viewer?.setGhostMode(ghostMode.value);
        if (selectedNode.value?.globalId) {
            viewer
                ?.isolate([selectedNode.value.globalId], selectedNode.value.globalId)
                .catch(() => {});
        }
    }

    async function invertIsolation() {
        if (!selectedNode.value?.globalId) return;
        await viewer?.isolate([selectedNode.value.globalId], selectedNode.value.globalId, true);
    }

    function clearMeasurements() {
        viewer?.clearMeasurements();
    }

    function toggleWireframe() {
        // Delegate to IfcViewerCore — it knows how to walk the fragments
        // model's mesh tree, flip wireframe on both single and array
        // materials, mark them needsUpdate, and force a fragments repaint.
        const enabled = viewer?.toggleWireframe() ?? false;
        statusText.value = `Wireframe ${enabled ? 'on' : 'off'}`;
    }

    // ── Tree ──────────────────────────────────────────────────────────────────
    // Tree is now built server-side via IfcApi.ParseStream (xBIM walks the
    // full spatial hierarchy). loadFromBytes uploads the file and consumes
    // the streamed result. Property reads/writes still go through web-ifc on
    // the client.

    function onTreeToggle(node: IfcNodeDto) {
        if (expandedIds[node.expressId]) delete expandedIds[node.expressId];
        else expandedIds[node.expressId] = true;
    }

    function onTreeSelect(node: IfcNodeDto) {
        suppressClear = true;
        selectByNode(node);
        if (node.globalId) viewer?.highlightByGlobalId(node.globalId).catch(() => {});
        setTimeout(() => {
            suppressClear = false;
        }, 300);
    }

    /** Open the imported article that corresponds to this tree node in
     *  Spheeres' record screen (modal). Same PubSub channel the importer
     *  page uses, so behaviour stays consistent. */
    function onTreeOpenExisting(node: IfcNodeDto) {
        if (!node.globalId || !existingEntityId.value) return;
        const recordId = existingRecords.value[node.globalId];
        if (!recordId) return;
        (
            window as unknown as { PubSub?: { publish: (ch: string, p: unknown) => void } }
        ).PubSub?.publish('GO_TO_RECORD_SCREEN', {
            EntityId: existingEntityId.value,
            RecordId: recordId,
            IsOpenInModal: true,
        });
    }

    // ── Selection + properties ────────────────────────────────────────────────
    function selectByNode(node: IfcNodeDto) {
        loadProperties(node);
        selectedNode.value = node;
        statusText.value = `Selected: ${node.name || node.ifcClass}`;
    }

    /**
     * Build the per-property edit map for the selected node by reading the
     * IFC structure via web-ifc. The displayed propertySets/quantitySets/
     * materials are already populated by the server during ParseStream — we
     * only need the express ids of individual IfcPropertySingleValue lines so
     * onPropertyChanged() can write edits back through ifcApi.WriteLine.
     */
    async function loadProperties(node: IfcNodeDto) {
        if (!(await ensureEditorModel())) return;
        const expressId = node.expressId;
        propEditMap.clear();

        try {
            const relDefines = ifcApi.GetLineIDsWithType(modelID, WebIFC.IFCRELDEFINESBYPROPERTIES);
            for (let i = 0; i < relDefines.size(); i++) {
                try {
                    const rel = ifcApi.GetLine(modelID, relDefines.get(i));
                    if (!rel.RelatedObjects) continue;
                    const relatedIds = Array.isArray(rel.RelatedObjects)
                        ? rel.RelatedObjects.map((o: any) => o.value)
                        : [rel.RelatedObjects.value];
                    if (!relatedIds.includes(expressId)) continue;

                    const setRef = rel.RelatingPropertyDefinition;
                    if (!setRef?.value) continue;
                    const set = ifcApi.GetLine(modelID, setRef.value);
                    if (!set.HasProperties) continue;

                    const setName = set.Name?.value || 'Properties';
                    const items = Array.isArray(set.HasProperties)
                        ? set.HasProperties
                        : [set.HasProperties];
                    for (const ref of items) {
                        if (!ref?.value) continue;
                        try {
                            const prop = ifcApi.GetLine(modelID, ref.value);
                            const propName = prop.Name?.value || '';
                            if (propName) {
                                propEditMap.set(`${setName}::${propName}`, {
                                    expressId: ref.value,
                                    propName: 'NominalValue',
                                });
                            }
                        } catch {
                            /* skip malformed property */
                        }
                    }
                } catch {
                    /* skip malformed relation */
                }
            }
        } catch {}
    }

    async function onPropertyChanged(payload: {
        setName: string;
        prop: IfcPropertyDto;
        newValue: string;
    }) {
        if (!(await ensureEditorModel())) return;
        const meta = propEditMap.get(`${payload.setName}::${payload.prop.name}`);
        if (!meta) return;
        try {
            const line = ifcApi.GetLine(modelID, meta.expressId);
            if (line[meta.propName] && line[meta.propName].value !== undefined) {
                line[meta.propName].value = payload.newValue;
                ifcApi.WriteLine(modelID, line);
                payload.prop.value = payload.newValue;
                statusText.value = `Updated: ${payload.prop.name}`;
            }
        } catch (err) {
            notifier.Failed(`Failed to update property: ${(err as Error).message}`);
        }
    }

    function clearSelection() {
        selectedNode.value = null;
    }

    // ── bSDD info modal ───────────────────────────────────────────────────────
    let bsddModal: Modal | null = null;
    let bsddVueApp: App | null = null;

    function openBsddModal() {
        if (!selectedNode.value) return;

        // Dispose any prior instance so clicking different nodes replaces content.
        bsddVueApp?.unmount();
        bsddVueApp = null;
        bsddModal?.Destroy();

        const modal = new Modal(
            {
                minWidth: '600px',
                maxWidth: '960px',
                minHeight: '400px',
                closeButton: 'box',
                closeOnEsc: true,
            },
            false
        );

        const mountPoint = document.createElement('div');
        modal.SetContent($(mountPoint));

        bsddVueApp = createApp(BsddInfoModal, { node: selectedNode.value });
        bsddVueApp.mount(mountPoint);

        modal.On('CLOSE', null, () => {
            bsddVueApp?.unmount();
            bsddVueApp = null;
            bsddModal = null;
        });

        bsddModal = modal;
        modal.Show();
    }

    // ── Close ────────────────────────────────────────────────────────────────
    function close() {
        emit('close');
    }

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(IfcWorkbench, {
    ref_key: "workbench",
    ref: workbench,
    height: __props.inModal ? 'auto' : 800
  }, {
    toolbar: _withCtx(() => [
      _createCommentVNode(" Editor-specific actions: file ops + transform.\r\n                 In modal mode the host already supplied bytes — no point opening another file. "),
      (!__props.inModal)
        ? (_openBlock(), _createElementBlock("button", {
            key: 0,
            class: "btn btn-default btn-sm ifc-toolbar-btn",
            title: "Open IFC File",
            onClick: openFile
          }, [...(_cache[6] || (_cache[6] = [
            _createElementVNode("i", { class: "fa fa-folder-open" }, null, -1 /* CACHED */)
          ]))]))
        : _createCommentVNode("v-if", true),
      _createElementVNode("button", {
        class: "btn btn-default btn-sm ifc-toolbar-btn",
        title: "Export IFC",
        disabled: !modelLoaded.value,
        onClick: exportIfc
      }, [...(_cache[7] || (_cache[7] = [
        _createElementVNode("i", { class: "fa fa-download" }, null, -1 /* CACHED */)
      ]))], 8 /* PROPS */, _hoisted_1),
      _cache[12] || (_cache[12] = _createElementVNode("span", { class: "ifc-toolbar-sep" }, null, -1 /* CACHED */)),
      _createElementVNode("button", {
        class: _normalizeClass(["btn btn-sm ifc-toolbar-btn", activeTool.value === 'select' ? 'btn-info' : 'btn-default']),
        title: "Select (Esc)",
        onClick: _cache[0] || (_cache[0] = ($event: any) => (setTool('select')))
      }, [...(_cache[8] || (_cache[8] = [
        _createElementVNode("i", { class: "fa fa-mouse-pointer" }, null, -1 /* CACHED */)
      ]))], 2 /* CLASS */),
      _createElementVNode("button", {
        class: _normalizeClass(["btn btn-sm ifc-toolbar-btn", activeTool.value === 'move' ? 'btn-info' : 'btn-default']),
        disabled: !modelLoaded.value,
        title: "Transform",
        onClick: _cache[1] || (_cache[1] = ($event: any) => (setTool('move')))
      }, [...(_cache[9] || (_cache[9] = [
        _createElementVNode("i", { class: "fa fa-arrows" }, null, -1 /* CACHED */)
      ]))], 10 /* CLASS, PROPS */, _hoisted_2),
      (activeTool.value === 'move')
        ? (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
            _createElementVNode("button", {
              class: _normalizeClass(["btn btn-sm ifc-toolbar-btn", transformMode.value === 'translate' ? 'btn-info' : 'btn-default']),
              title: "Move (G)",
              onClick: _cache[2] || (_cache[2] = ($event: any) => (setTransformMode('translate')))
            }, "G", 2 /* CLASS */),
            _createElementVNode("button", {
              class: _normalizeClass(["btn btn-sm ifc-toolbar-btn", transformMode.value === 'rotate' ? 'btn-info' : 'btn-default']),
              title: "Rotate (R)",
              onClick: _cache[3] || (_cache[3] = ($event: any) => (setTransformMode('rotate')))
            }, "R", 2 /* CLASS */),
            _createElementVNode("button", {
              class: _normalizeClass(["btn btn-sm ifc-toolbar-btn", transformMode.value === 'scale' ? 'btn-info' : 'btn-default']),
              title: "Scale (S)",
              onClick: _cache[4] || (_cache[4] = ($event: any) => (setTransformMode('scale')))
            }, "S", 2 /* CLASS */)
          ], 64 /* STABLE_FRAGMENT */))
        : _createCommentVNode("v-if", true),
      _createCommentVNode(" Wireframe button hidden for now: fragments' custom geometry\r\n                 layout doesn't support three's standard wireframe path, and\r\n                 the postproduction-edges alternative needs more design work.\r\n                 Code paths in IfcViewerCore.setWireframe + IfcViewer.toggleWireframe\r\n                 are kept so we can re-enable this later. "),
      false
        ? (_openBlock(), _createElementBlock("button", {
            key: 2,
            class: "btn btn-default btn-sm ifc-toolbar-btn",
            disabled: !modelLoaded.value,
            title: "Toggle Wireframe",
            onClick: toggleWireframe
          }, [...(_cache[10] || (_cache[10] = [
            _createElementVNode("i", { class: "fa fa-th" }, null, -1 /* CACHED */)
          ]))], 8 /* PROPS */, _hoisted_3))
        : _createCommentVNode("v-if", true),
      _createCommentVNode(" Shared viewer toolbar — same component the importer uses. "),
      _cache[13] || (_cache[13] = _createElementVNode("span", { class: "ifc-toolbar-sep" }, null, -1 /* CACHED */)),
      _createVNode(IfcViewerToolbar, {
        "model-loaded": modelLoaded.value,
        "has-selection": !!selectedNode.value,
        "active-tool": 
                    activeTool.value === 'length' ||
                    activeTool.value === 'angle' ||
                    activeTool.value === 'area' ||
                    activeTool.value === 'clip'
                        ? activeTool.value
                        : 'none'
                ,
        onShowAll: showAll,
        onFit: fitView,
        onSetView: setView,
        onResetRotation: resetRotation,
        onToggleGhost: toggleGhost,
        onInvertIsolation: invertIsolation,
        onSetTool: _cache[5] || (_cache[5] = (tool) => setTool(tool)),
        onClearMeasurements: clearMeasurements
      }, null, 8 /* PROPS */, ["model-loaded", "has-selection", "active-tool"]),
      _cache[14] || (_cache[14] = _createElementVNode("span", { style: {"flex":"1","min-width":"8px"} }, null, -1 /* CACHED */)),
      (__props.inModal)
        ? (_openBlock(), _createElementBlock("button", {
            key: 3,
            class: "btn btn-default btn-sm ifc-toolbar-btn",
            title: "Close",
            style: {"margin-left":"8px"},
            onClick: close
          }, [...(_cache[11] || (_cache[11] = [
            _createElementVNode("i", { class: "fa fa-times" }, null, -1 /* CACHED */)
          ]))]))
        : _createCommentVNode("v-if", true)
    ]),
    "viewport-extras": _withCtx(() => [
      _createVNode(IfcClassVisibilityBar, {
        classes: allClasses.value,
        hidden: hiddenClasses,
        onToggle: toggleClassVisibility
      }, null, 8 /* PROPS */, ["classes", "hidden"])
    ]),
    tree: _withCtx(() => [
      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(tree.value, (root) => {
        return (_openBlock(), _createBlock(IfcTreeNode, {
          key: root.expressId,
          node: root,
          depth: 0,
          "expanded-ids": expandedIds,
          "selected-id": selectedNode.value?.expressId ?? -1,
          "existing-records": existingRecords.value,
          onToggle: onTreeToggle,
          onSelect: onTreeSelect,
          onOpenExisting: onTreeOpenExisting
        }, null, 8 /* PROPS */, ["node", "expanded-ids", "selected-id", "existing-records"]))
      }), 128 /* KEYED_FRAGMENT */))
    ]),
    "viewport-overlay": _withCtx(() => [
      (loading.value)
        ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
            _createElementVNode("div", _hoisted_5, [
              _cache[15] || (_cache[15] = _createElementVNode("i", { class: "fa fa-spinner fa-spin fa-2x" }, null, -1 /* CACHED */)),
              _createElementVNode("div", _hoisted_6, _toDisplayString(loadingMessage.value), 1 /* TEXT */),
              _createElementVNode("div", _hoisted_7, [
                _createElementVNode("div", {
                  class: "ifc-editor__progress-fill",
                  style: _normalizeStyle({ width: loadingPercent.value + '%' })
                }, null, 4 /* STYLE */)
              ])
            ])
          ]))
        : _createCommentVNode("v-if", true),
      _createElementVNode("input", {
        ref_key: "fileInput",
        ref: fileInput,
        type: "file",
        accept: ".ifc",
        style: {"display":"none"},
        onChange: onFileSelected
      }, null, 544 /* NEED_HYDRATION, NEED_PATCH */)
    ]),
    properties: _withCtx(() => [
      _createVNode(IfcPropertiesPanel, {
        node: selectedNode.value,
        editable: true,
        "show-header-extras": !!selectedNode.value,
        onPropertyChanged: onPropertyChanged
      }, {
        "header-extras": _withCtx(() => [
          _createElementVNode("button", {
            class: "btn btn-default btn-xs ifc-props__bsdd-btn",
            title: "Look up this class in the buildingSMART Data Dictionary",
            onClick: openBsddModal
          }, [...(_cache[16] || (_cache[16] = [
            _createElementVNode("i", { class: "fa fa-book" }, null, -1 /* CACHED */),
            _createTextVNode(" bSDD info ", -1 /* CACHED */)
          ]))])
        ]),
        _: 1 /* STABLE */
      }, 8 /* PROPS */, ["node", "show-header-extras"])
    ]),
    _: 1 /* STABLE */
  }, 8 /* PROPS */, ["height"]))
}
}

})